import type { FaqEntry } from "@/lib/json-ld";

export interface FactRow {
  label: string;
  value: string;
}

export const PROGRAM_FACTS: FactRow[] = [
  { label: "Starting commission", value: "20%" },
  { label: "Silver commission", value: "30%" },
  { label: "Gold commission", value: "40%" },
  { label: "Customer coupon", value: "20% off" },
  { label: "Maximum commission", value: "Up to $10,000 per qualifying referral" },
  { label: "Referral tracking period", value: "30 days" },
  { label: "Minimum payout balance", value: "$100" },
];

export interface HowItWorksStep {
  title: string;
  body: string;
}

export const HOW_IT_WORKS_STEPS: HowItWorksStep[] = [
  {
    title: "Get Your Referral Link",
    body: "Create a Proxidize account or sign in to your existing account. Every account is enrolled in the Referral Program subject to the program rules. Open the referral section to access your unique referral link and the 20% customer coupon you can share.",
  },
  {
    title: "Recommend Proxidize",
    body: "Share Proxidize through approved channels with an audience that may benefit from managed residential or mobile proxy infrastructure. Describe the service accurately and clearly disclose your commercial relationship with Proxidize.",
  },
  {
    title: "Earn Commission",
    body: "When an eligible new customer follows your referral link and purchases a subscription within the 30-day tracking period, the referral can qualify for commission. Eligible subsequent renewals can continue generating commission while the referred subscription remains active.",
  },
];

export interface CommissionTierRow {
  tier: string;
  rate: string;
  how: string;
}

export const COMMISSION_TIERS: CommissionTierRow[] = [
  { tier: "Standard", rate: "20%", how: "Starting rate for participating affiliates" },
  {
    tier: "Silver",
    rate: "30%",
    how: "Higher rate for eligible affiliates with growing referral performance",
  },
  { tier: "Gold", rate: "40%", how: "Maximum rate for eligible top-performing affiliates" },
];

export interface EarningsRow {
  payment: string;
  standard: string;
  silver: string;
  gold: string;
}

export const EARNINGS_EXAMPLES: EarningsRow[] = [
  { payment: "$50", standard: "$10", silver: "$15", gold: "$20" },
  { payment: "$100", standard: "$20", silver: "$30", gold: "$40" },
  { payment: "$500", standard: "$100", silver: "$150", gold: "$200" },
  { payment: "$1,000", standard: "$200", silver: "$300", gold: "$400" },
];

export const PAYOUT_DETAILS: FactRow[] = [
  { label: "Tracking period", value: "30 days from the referral-link click" },
  { label: "Minimum payout", value: "$100" },
  { label: "Payout frequency", value: "Monthly" },
  { label: "Waiting period", value: "The applicable 30-day waiting period must be satisfied" },
  {
    label: "Renewal commission",
    value: "Applies to eligible renewals while the referred subscription remains active",
  },
  {
    label: "Available payment methods",
    value: "Bank transfer, PayPal, instant debit transfer, and Venmo",
  },
  { label: "Standard transfer fees", value: "Covered by Proxidize" },
];

export const PROMOTION_APPROPRIATE: string[] = [
  "An honest review or tutorial containing a clear affiliate disclosure.",
  "A resource page recommending proxy infrastructure.",
  "Organic social content for a relevant professional audience.",
  "A newsletter sent to recipients who agreed to receive it.",
  "A direct recommendation to a customer or business contact who needs the service.",
];

export const PROMOTION_RESTRICTED: string[] = [
  "Using your own referral link for personal purchases.",
  "Sending spam or unsolicited commercial messages.",
  "Publishing referral coupons on coupon or discount-aggregation websites.",
  "Cookie stuffing or manipulating referral tracking.",
  "Making misleading or unauthorized product claims.",
  "Impersonating Proxidize or copying its website.",
  "Creating domains containing Proxidize trademarks or misspellings.",
  "Offering unauthorized cash back, discounts, or other incentives.",
  "Running paid or boosted advertising without prior written approval.",
  "Bidding on Proxidize trademarks in paid search.",
  "Reselling or sublicensing Proxidize without authorization.",
];

export interface ProgramComparisonRow {
  program: string;
  purpose: string;
  availability: string;
}

export const PROGRAM_VS_PARTNER: ProgramComparisonRow[] = [
  {
    program: "Referral Program",
    purpose: "Recommend Proxidize and earn commission from eligible referrals",
    availability: "Available through a Proxidize account",
  },
  {
    program: "Partner Program",
    purpose: "Reseller, integration, and strategic partnership arrangements",
    availability: "Closed to new applications",
  },
];

export const REFERRAL_FAQS: FaqEntry[] = [
  {
    q: "How much commission does the Proxidize Referral Program pay?",
    a: "The Standard rate is 20%. Eligible Silver affiliates can earn 30%, and eligible Gold affiliates can earn 40%. Commission remains subject to the Referral Program Terms of Use.",
  },
  {
    q: "How do I qualify for Silver or Gold?",
    a: "Silver and Gold rates are available to eligible affiliates as their referral performance grows. Tier changes are subject to Proxidize approval. Your current rate should be confirmed through your account or direct communication from Proxidize.",
  },
  {
    q: "How much can I earn from one referral?",
    a: "An affiliate can earn up to $10,000 in total commission from each qualifying referral, subject to the program rules.",
  },
  {
    q: "Does commission apply to subscription renewals?",
    a: "Eligible subsequent renewals can continue earning commission while the referred customer's subscription remains active and the referral remains valid under the program rules.",
  },
  {
    q: "How long does referral tracking last?",
    a: "The referral period lasts 30 days from the customer's click on the affiliate's unique referral link.",
  },
  {
    q: "What is the minimum payout?",
    a: "The minimum payout balance is $100. Eligible balances are processed according to the monthly payment schedule after the applicable waiting period has been satisfied.",
  },
  {
    q: "Does Proxidize have a referral coupon?",
    a: "Yes. Participating affiliates receive a 20% Proxidize coupon to share with eligible new customers. Coupon use remains subject to the program rules.",
  },
  {
    q: "Can existing Proxidize users participate?",
    a: "Yes. Proxidize account holders are enrolled in the Referral Program subject to the program rules. Existing users should sign in to access their referral information rather than creating another account.",
  },
  {
    q: "Can I use paid advertising to promote my referral link?",
    a: "Paid or boosted promotion requires prior written approval from Proxidize for each campaign or placement. Affiliates may not bid on Proxidize trademarks or use the Proxidize website as a paid-ad destination without approval.",
  },
  {
    q: "Can I refer myself?",
    a: "No. Purchases made through your own referral link do not qualify for commission.",
  },
  {
    q: "Is this also the Proxidize affiliate program?",
    a: "Yes. Proxidize calls the offer its Referral Program, while participating referrers may also be described as affiliates in the program rules.",
  },
];
